import * as THREE from 'three';
import ActorFactory from './ActorFactory';
import TankActor from './actors/TankActor';
import { IActor, IAimable } from './types';

type PositionDTO = { x: number; y: number; z: number };
type RotationDTO = { x: number; y: number; z: number; w: number };
export type ServerPlayerUpdate = {
  position?: PositionDTO;
  rotation?: RotationDTO;
  turretRotationX?: number;
};

export default class Player {
  scene: THREE.Scene;
  name: string;
  userId: string;
  color: string;
  tankModel: THREE.Object3D | null; // kept for backwards compatibility (points to actor root)
  collidables: THREE.Object3D[];
  actor: IActor;

  constructor(
    scene: THREE.Scene,
    name: string,
    userId: string,
    color: string,
    collidables: THREE.Object3D[]
  ) {
    this.scene = scene;
    this.name = name;
    this.userId = userId;
    this.color = color;
    this.tankModel = null;
    this.collidables = collidables;

    // For now default to 'tank' actor; future: pass playerType from server
    this.actor = ActorFactory.create('tank', this.scene, this.collidables, this.name, this.userId, this.color);
    this.tankModel = this.actor.getRoot();
  }

  // Kept for compatibility; delegates to actor
  // Initializes the player's tank model and adds it to the scene.
  initTankModel() {}

  // Sets the tank's skin/texture
  setSkin(skinName: string) {
    this.actor.setSkin(skinName);
  }

  // Sets the tank's position based on multiplayer data.
  setPosition(position: THREE.Vector3) {
    this.actor.setPosition(position);
  }

  // Sets the tank's rotation based on multiplayer data.
  setRotation(rotation: THREE.Quaternion) {
    this.actor.setRotation(rotation);
  }

  // Sets the turret's rotation independently of the tank body.
  setTurretRotation(rotationX: number) {
    const aimable = this.actor as unknown as IAimable;
    if (aimable && typeof aimable.setTurretRotationX === 'function') {
      aimable.setTurretRotationX(rotationX);
    }
  }

  // Removes the player from the scene and cleans up resources.
  remove() {
    this.actor.dispose();
    this.tankModel = null;
    console.log(`Player ${this.name} (${this.userId}) removed from scene`);
  }

  // Updates the tank and turret based on the latest state from server.
  updateFromServer(data: ServerPlayerUpdate) {
    if (data.position) {
      this.setPosition(
        new THREE.Vector3(data.position.x, data.position.y, data.position.z)
      );
    }
    if (data.rotation) {
      this.setRotation(
        new THREE.Quaternion(
          data.rotation.x,
          data.rotation.y,
          data.rotation.z,
          data.rotation.w
        )
      );
    }
    if (typeof data.turretRotationX === 'number') {
      this.setTurretRotation(data.turretRotationX);
    }
  }

  receiveDamage = (damage: number, attackedId: string) => {
    console.log(`Damage ${damage} from player ${attackedId}`);
  };

  death = () => {
    console.log('I was killed');
    this.remove();
  };
}

